import axios from 'src/utils/axios';
import parse from 'src/utils/parse';
import logger from 'src/utils/logger';
import type {
  CreateAgentData,
  SecurityAgentApiRow,
  SecurityAgentsListEnvelope,
  SecurityAgentsStatusQuery,
  SecurityAgentsStatusSummaryEnvelope,
  WazuhAgentDeploymentEligibilityData,
  WazuhAgentInstallationCommandsData,
} from 'src/types/mindtime/wazuhAgents';
import type {
  ThreatHuntingOverviewApiEnvelope,
  ThreatHuntingOverviewData,
  ThreatHuntingOverviewQuery,
} from 'src/types/mindtime/threatHuntingOverview';
import type {
  SecurityOverviewApiEnvelope,
  SecurityOverviewData,
} from 'src/types/mindtime/wazuhOverview';
import { WazuhDashboardAPI } from './wazuhDashboardApi';

export interface CreateAgentPayload {
  agentName: string;
  osType: string;
}

export interface DeleteAgentsPayload {
  systemWazuhAgentIds: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const readAgentsListFailureMessage = (raw: unknown): string | null => {
  if (!isRecord(raw) || raw.success !== false) return null;
  return typeof raw.message === 'string'
    ? raw.message
    : 'Agents list request was not successful';
};

const tryParseAgentsList = (raw: unknown): SecurityAgentApiRow[] | null => {
  if (!isRecord(raw)) return null;

  if (typeof raw.success === 'boolean') {
    if (!raw.success) return null;
    const { data } = raw;
    if (Array.isArray(data)) return data as SecurityAgentApiRow[];
    if (isRecord(data) && Array.isArray(data.items)) {
      return data.items as SecurityAgentApiRow[];
    }
    return null;
  }

  if (Array.isArray(raw)) return raw as SecurityAgentApiRow[];
  if (Array.isArray(raw.items)) return raw.items as SecurityAgentApiRow[];
  return null;
};

const parseDeploymentEligibility = (raw: unknown): WazuhAgentDeploymentEligibilityData => {
  if (!isRecord(raw)) {
    throw new Error('Invalid response: missing deployment eligibility envelope');
  }

  const envelopeMessage =
    typeof raw.message === 'string' && raw.message.trim() ? raw.message.trim() : undefined;

  const { data } = raw;

  if (typeof data === 'boolean') {
    return { eligible: data, message: envelopeMessage };
  }

  if (isRecord(data) && typeof data.eligible === 'boolean') {
    const nestedMessage =
      typeof data.message === 'string' && data.message.trim()
        ? data.message.trim()
        : undefined;
    return {
      eligible: data.eligible,
      message: envelopeMessage || nestedMessage,
      licenseCount: typeof data.licenseCount === 'number' ? data.licenseCount : undefined,
      agentCount: typeof data.agentCount === 'number' ? data.agentCount : undefined,
    };
  }

  if (raw.success === false) {
    throw new Error(envelopeMessage || 'Deployment eligibility request was not successful');
  }

  throw new Error('Invalid response: missing deployment eligibility data');
};

const readInstallCommands = (value: Record<string, unknown>): string[] | null => {
  const commands = value.installCommands ?? value.install_commands;
  return Array.isArray(commands) ? (commands as string[]) : null;
};

const normalizeCreateAgentData = (raw: Record<string, unknown>): CreateAgentData => {
  const installCommands = readInstallCommands(raw);
  if (!installCommands || installCommands.length === 0) {
    throw new Error('No installation commands received from the server.');
  }

  return {
    agentId: String(raw.agentId ?? raw.agent_id ?? ''),
    agentName: String(raw.agentName ?? raw.agent_name ?? ''),
    enrollmentKey: String(raw.enrollmentKey ?? raw.enrollment_key ?? ''),
    osType: String(raw.osType ?? raw.os_type ?? ''),
    managerHost: String(raw.managerHost ?? raw.manager_host ?? ''),
    groupName: (raw.groupName ?? raw.group_name ?? null) as string | null,
    installScript: String(raw.installScript ?? raw.install_script ?? ''),
    installCommands,
  };
};

const parseCreateAgentResponse = (raw: unknown): CreateAgentData => {
  if (!isRecord(raw)) {
    throw new Error('Invalid response: empty create-agent body');
  }

  if (typeof raw.success === 'boolean') {
    if (!raw.success) {
      throw new Error(
        typeof raw.message === 'string'
          ? raw.message
          : 'Create agent request was not successful'
      );
    }

    if (!isRecord(raw.data)) {
      throw new Error('Invalid response: create-agent payload missing data');
    }

    return normalizeCreateAgentData(raw.data);
  }

  const nestedData = raw.data;
  if (isRecord(nestedData) && readInstallCommands(nestedData)) {
    return normalizeCreateAgentData(nestedData);
  }

  if (readInstallCommands(raw)) {
    return normalizeCreateAgentData(raw);
  }

  if (typeof raw.message === 'string' && raw.message.trim()) {
    throw new Error(raw.message);
  }

  throw new Error('Invalid response: missing create-agent envelope');
};

const normalizeInstallationCommandsData = (
  raw: Record<string, unknown>
): WazuhAgentInstallationCommandsData => {
  const installCommands = readInstallCommands(raw) ?? [];
  const systemWazuhAgentId = String(
    raw.systemWazuhAgentId ?? raw.system_wazuh_agent_id ?? ''
  ).trim();

  return {
    ...(systemWazuhAgentId ? { systemWazuhAgentId } : {}),
    installCommands,
  };
};

const parseInstallationCommandsResponse = (
  raw: unknown
): WazuhAgentInstallationCommandsData => {
  if (!isRecord(raw)) {
    throw new Error('Invalid response: empty installation-commands body');
  }

  if (typeof raw.success === 'boolean') {
    if (!raw.success) {
      throw new Error(
        typeof raw.message === 'string'
          ? raw.message
          : 'Installation commands request was not successful'
      );
    }

    if (Array.isArray(raw.data)) {
      return { installCommands: raw.data as string[] };
    }

    if (!isRecord(raw.data)) {
      throw new Error('Invalid response: installation-commands payload missing data');
    }

    return normalizeInstallationCommandsData(raw.data);
  }

  if (Array.isArray(raw)) {
    return { installCommands: raw as string[] };
  }

  const nestedData = raw.data;
  if (isRecord(nestedData) && readInstallCommands(nestedData)) {
    return normalizeInstallationCommandsData(nestedData);
  }

  if (readInstallCommands(raw)) {
    return normalizeInstallationCommandsData(raw);
  }

  if (typeof raw.message === 'string' && raw.message.trim()) {
    throw new Error(raw.message);
  }

  throw new Error('Invalid response: missing installation-commands envelope');
};

class WazuhAPI {
  readonly dashboard: WazuhDashboardAPI;

  constructor() {
    this.dashboard = new WazuhDashboardAPI(() => this.getBaseUrl());
  }

  private getBaseUrl(): string {
    const rawBaseUrl = process.env.REACT_APP_WAZUH_SERVICE;
    if (!rawBaseUrl) {
      throw new Error('REACT_APP_WAZUH_SERVICE environment variable is not defined');
    }
    return rawBaseUrl.replace(/\/+$/, '');
  }

  async getThreatHuntingOverview(
    query: ThreatHuntingOverviewQuery
  ): Promise<ThreatHuntingOverviewData> {
    return new Promise((resolve, reject) => {
      let baseUrl = '';
      try {
        baseUrl = this.getBaseUrl();
      } catch (error) {
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .get(`${baseUrl}/api/v1/threat-hunting/overview`, { params: query })
        .then((response) => {
          const parsedResponse = parse(response);
          const body = parsedResponse?.data as ThreatHuntingOverviewApiEnvelope | undefined;

          if (!body || typeof body.success !== 'boolean') {
            throw new Error('Invalid response: missing threat-hunting overview envelope');
          }
          if (!body.success || !body.data) {
            throw new Error(body.message || 'Threat-hunting overview request was not successful');
          }

          resolve(body.data);
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async getOverview(): Promise<SecurityOverviewData> {
    return new Promise((resolve, reject) => {
      let baseUrl = '';
      try {
        baseUrl = this.getBaseUrl();
      } catch (error) {
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .get(`${baseUrl}/api/v1/overview`)
        .then((response) => {
          const parsedResponse = parse(response);
          const body = parsedResponse?.data as SecurityOverviewApiEnvelope | undefined;

          if (!body || typeof body.success !== 'boolean') {
            throw new Error('Invalid response: missing overview envelope');
          }
          if (!body.success || !body.data) {
            throw new Error(body.message || 'Overview request was not successful');
          }

          resolve(body.data);
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async getAgentsStatusSummary(): Promise<SecurityAgentsStatusSummaryEnvelope['data']> {
    return new Promise((resolve, reject) => {
      let baseUrl = '';
      try {
        baseUrl = this.getBaseUrl();
      } catch (error) {
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .get(`${baseUrl}/api/v1/agents/summary/status`)
        .then((response) => {
          const parsedResponse = parse(response);
          const body = parsedResponse?.data as SecurityAgentsStatusSummaryEnvelope | undefined;

          if (!body || typeof body.success !== 'boolean') {
            throw new Error('Invalid response: missing agents status summary envelope');
          }
          if (!body.success || body.data == null) {
            throw new Error(body.message || 'Agents status summary request was not successful');
          }

          resolve(body.data);
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async getDeploymentEligibility(): Promise<WazuhAgentDeploymentEligibilityData> {
    return new Promise((resolve, reject) => {
      let baseUrl = '';
      try {
        baseUrl = this.getBaseUrl();
      } catch (error) {
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .get(`${baseUrl}/api/v1/agents/deployment-eligibility`)
        .then((response) => {
          const parsedResponse = parse(response);
          resolve(parseDeploymentEligibility(parsedResponse?.data));
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async getAgents(params?: { status?: SecurityAgentsStatusQuery }): Promise<SecurityAgentsListEnvelope['data']> {
    return new Promise((resolve, reject) => {
      let baseUrl = '';
      try {
        baseUrl = this.getBaseUrl();
      } catch (error) {
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .get(`${baseUrl}/api/v1/agents`, { params })
        .then((response) => {
          const parsedResponse = parse(response);
          const top = parsedResponse?.data as SecurityAgentsListEnvelope | Record<string, unknown> | undefined;
          const nested = isRecord(top) ? top.data : undefined;
          const failureMessage =
            readAgentsListFailureMessage(top) ?? readAgentsListFailureMessage(nested);

          if (failureMessage) {
            throw new Error(failureMessage);
          }

          const rows = tryParseAgentsList(top) ?? tryParseAgentsList(nested);

          if (rows === null) {
            throw new Error('Invalid response: missing agents list envelope');
          }

          resolve(rows);
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async createAgent(payload: CreateAgentPayload): Promise<CreateAgentData> {
    return new Promise((resolve, reject) => {
      let baseUrl = '';
      try {
        baseUrl = this.getBaseUrl();
      } catch (error) {
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .post(`${baseUrl}/api/v1/agents`, payload)
        .then((response) => {
          const parsedResponse = parse(response);
          resolve(parseCreateAgentResponse(parsedResponse?.data));
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async getInstallationCommands(
    systemWazuhAgentId: string
  ): Promise<WazuhAgentInstallationCommandsData> {
    return new Promise((resolve, reject) => {
      let baseUrl = '';
      try {
        baseUrl = this.getBaseUrl();
      } catch (error) {
        logger(error, 'error');
        reject(error);
        return;
      }

      const id = systemWazuhAgentId.trim();
      if (!id) {
        reject(new Error('systemWazuhAgentId is required'));
        return;
      }

      axios
        .get(`${baseUrl}/api/v1/agents/getInstallationCommands/${encodeURIComponent(id)}`)
        .then((response) => {
          const parsedResponse = parse(response);
          resolve(parseInstallationCommandsResponse(parsedResponse?.data));
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async patchAgentHostname(
    systemWazuhAgentId: string,
    hostName: string
  ): Promise<{
    systemWazuhAgentId?: string;
    wazuhAgentId?: string;
    name?: string;
    hostName?: string;
  }> {
    return new Promise((resolve, reject) => {
      let baseUrl = '';
      try {
        baseUrl = this.getBaseUrl();
      } catch (error) {
        logger(error, 'error');
        reject(error);
        return;
      }

      const id = systemWazuhAgentId.trim();
      if (!id) {
        reject(new Error('systemWazuhAgentId is required'));
        return;
      }

      axios
        .patch(`${baseUrl}/api/v1/agents/${encodeURIComponent(id)}/hostname`, { hostName })
        .then((response) => {
          const parsedResponse = parse(response);
          const data = parsedResponse?.data?.data ?? parsedResponse?.data;
          if (!data || typeof data !== 'object') {
            reject(new Error('Invalid response: missing hostname update payload'));
            return;
          }
          const row = data as Record<string, unknown>;
          resolve({
            systemWazuhAgentId:
              typeof row.systemWazuhAgentId === 'string' ? row.systemWazuhAgentId : undefined,
            wazuhAgentId: typeof row.wazuhAgentId === 'string' ? row.wazuhAgentId : undefined,
            name: typeof row.name === 'string' ? row.name : undefined,
            hostName: typeof row.hostName === 'string' ? row.hostName : undefined,
          });
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async deleteAgents(payload: DeleteAgentsPayload): Promise<void> {
    return new Promise((resolve, reject) => {
      let baseUrl = '';
      try {
        baseUrl = this.getBaseUrl();
      } catch (error) {
        logger(error, 'error');
        reject(error);
        return;
      }

      axios
        .delete(`${baseUrl}/api/v1/agents`, { data: payload })
        .then(() => resolve())
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }
}

export const wazuhAPI = new WazuhAPI();
